<template>
  <v-card :loading="loading">
    <v-toolbar>
      <v-toolbar-title>
        Dealer Participants<span v-if="dealerName">: {{ dealerName }}</span>
        <span v-else-if="loading">: Loading...</span>
      </v-toolbar-title>
      <v-spacer />
      <v-btn
        small
        outlined
        color="primary"
        :loading="isExporting"
        :disabled="loading || !total"
        @click="exportParticipants"
      >
        <v-icon left small>mdi-download</v-icon>
        Export
      </v-btn>
    </v-toolbar>
    <v-card-text>
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :options.sync="options"
        :server-items-length="total"
        dense
        no-data-text="No participants found for this dealer."
      >
        <template v-slot:item.user.firstName="{ item }"> {{ item.user.fullName }} </template>

        <template v-slot:item.status="{ item }"> {{ item.status.description }} </template>

        <template v-slot:item.requiredFieldsMissing="{ item }">
          <v-icon v-if="!item.requiredFieldsMissing || item.requiredFieldsMissing.length === 0" color="green"
            >mdi-check</v-icon
          >
          <v-tooltip v-else bottom>
            <template #activator="{ on, attrs }">
              <v-icon color="red" v-bind="attrs" v-on="on">mdi-close</v-icon>
            </template>
            <span>{{ $participant.getProfileCompletionText(item) }}</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: "DealerParticipants",
  data() {
    return {
      loading: true,
      isExporting: false,
      dealer: null,
      items: [],
      total: 0,
      options: {
        itemsPerPage: 10,
        page: 1,
        sortBy: ["participantKey"],
        sortDesc: [false]
      },
      headers: [
        { text: this.$i18n.translate("Key"), value: "participantKey" },
        { text: this.$i18n.translate("Name"), value: "user.firstName", sortable: true },
        { text: this.$i18n.translate("Status"), value: "status", sortable: true },
        {
          text: this.$i18n.translate("Profile Complete?"),
          value: "requiredFieldsMissing",
          sortable: false,
          align: "center"
        }
      ]
    };
  },
  computed: {
    dealerName() {
      return this.dealer ? `${this.dealer.name} (${this.dealer.organizationKey})` : "";
    }
  },
  watch: {
    options: {
      handler() {
        this.fetchData();
      },
      deep: true
    }
  },
  methods: {
    async fetchData() {
      const dealerId = this.$route.query.dealerId;
      if (!dealerId) return;

      this.loading = true;
      const params = this.$util.buildPaginationParams(this.options);

      try {
        if (!this.dealer) {
          const { data } = await this.$api.get(`/api/organizations/${dealerId}`);
          this.dealer = data;
        }

        const { data } = await this.$api.post(`/api/participants/search?${params.toString()}`, {
          organization: { id: dealerId },
          isEffective: "Active"
        });

        this.items = data.content;
        this.total = data.totalElements;
      } catch {
        this.items = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    async exportParticipants() {
      this.isExporting = true;
      try {
        const data = await this.$util.fetchPaginatedData(
          "/api/participants/search",
          {
            organization: { id: this.$route.query.dealerId },
            isEffective: "Active"
          },
          100,
          ["user", "participantType", "status"]
        );
        const columns = this.headers.map(header => {
          if (header.value === "user.firstName") {
            return { ...header, format: row => row.user?.fullName || "" };
          }
          if (header.value === "status") {
            return { ...header, format: row => row.status?.description || "" };
          }
          if (header.value === "requiredFieldsMissing") {
            return {
              ...header,
              format: row => this.$participant.getProfileCompletionText(row)
            };
          }
          return header;
        });
        this.$export.exportToCsv(`${this.dealer?.organizationKey || "dealer"}_participants`, columns, data);
      } finally {
        this.isExporting = false;
      }
    }
  }
};
</script>
